import React, { useEffect, useState, useMemo } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Button,
    alpha,
    CircularProgress,
    IconButton,
    Modal,
    Alert,
    Link,
    Paper,
    Divider,
    Chip,
    Avatar,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { getImageVotes, voteForImage, userHasVoted, getCompetitions, hasUserEnteredCompetition, removeCompetitionEntry } from 'src/apiCalls';
import { Image, Competition } from 'src/interfaces';
import { useUserContext } from 'src/components/ProtectedRoute/userProvider';
import logger from 'src/logger';

// --- Constants ---
const CONFETTI_DURATION_MS = 2500;
const SINGLE_IMAGE_BASE_URL = '/single?imageId=';

// --- Helper Functions ---

// Function to format date in a readable way
const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
};

// --- Helper Components ---

// Simple Confetti Animation Component
function ConfettiAnimation() {
    const CONFETTI_COUNT = 5;
    const confettiKeyframes = `
      @keyframes fall {
        0% {
          transform: translateY(0) rotate(0);
          opacity: 1;
        }
        100% {
          transform: translateY(100vh) rotate(720deg);
          opacity: 0;
        }
      }`;

    return (
        <>
            <style>{confettiKeyframes}</style>
            <Box
                sx={{
                    pointerEvents: 'none',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    overflow: 'hidden',
                    zIndex: 9999
                }}
            >
                {Array.from({ length: CONFETTI_COUNT }).map((_, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            position: 'absolute',
                            width: '20px',
                            height: '20px',
                            backgroundColor: (theme) =>
                                idx % 2
                                    ? theme.palette.secondary.main
                                    : theme.palette.primary.main,
                            animation: `fall ${CONFETTI_DURATION_MS / 1000}s linear`,
                            left: `${Math.random() * 100}vw`,
                            top: '-50px',
                            transform: `rotate(${Math.random() * 360}deg)`
                        }}
                    />
                ))}
            </Box>
        </>
    );
}

// Phase Information Component
const PhaseInfo = ({ competition, countdown }) => {
    const theme = useTheme();
    const startDate = new Date(competition.startDate);
    const endDate = new Date(competition.endDate);
    const now = new Date();
    const isActive = now >= startDate && now <= endDate;
    
    return (
        <Paper 
            elevation={0}
            sx={{ 
                p: 3, 
                mb: 4, 
                background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light}15 100%)`,
                borderRadius: 1,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
        >
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={isActive ? 6 : 12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CalendarMonthIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                        <Typography 
                            variant="h6" 
                            component="h2"
                            sx={{
                                fontWeight: 'bold',
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {isActive ? 'Competition Active' : 'Competition Ended'}
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        {isActive 
                            ? `Submit your best catch from ${formatDate(startDate)} to ${formatDate(endDate)}.` 
                            : `The competition has ended. Check back for new competitions!`}
                    </Typography>
                </Grid>
                
                {isActive && (
                    <Grid item xs={12} md={6}>
                        <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                            <Chip 
                                label="Active"
                                color="success"
                                sx={{ 
                                    mb: 1,
                                    fontWeight: 'bold',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    borderRadius: '2px'
                                }}
                            />
                            <Typography 
                                variant="h6" 
                                component="div" 
                                sx={{ 
                                    fontWeight: 'bold',
                                    color: theme.palette.primary.main
                                }}
                            >
                                {countdown}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Entries allowed now. Voting starts after entry phase.
                            </Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Paper>
    );
};

// Style for the Modal Box
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 600,
    maxHeight: '80vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    p: 4,
    borderRadius: 2,
    boxShadow: 24
};

// Image Card Component
const ImageCard = ({ image, onVote, hasVoted, isVoting, isCurrentUser }) => {
    const theme = useTheme();
    const [isRemoving, setIsRemoving] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const { user } = useUserContext();
    const { accessToken, uid } = user || {};
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const competitionId = queryParams.get('id');

    const handleRemoveEntry = async () => {
        if (!accessToken || !uid || !competitionId) return;
        
        try {
            setIsRemoving(true);
            await removeCompetitionEntry(accessToken, uid, image.id, competitionId);
            window.location.reload();
        } catch (err) {
            logger.error('Error removing competition entry:', err);
        } finally {
            setIsRemoving(false);
            setShowConfirmDialog(false);
        }
    };

    const handleRemoveClick = () => {
        setShowConfirmDialog(true);
    };

    const handleCloseDialog = () => {
        setShowConfirmDialog(false);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                position: 'relative',
                background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light}15 100%)`,
                borderRadius: '2px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 36px rgba(0, 0, 0, 0.15)',
                }
            }}
        >
            {/* User Indicator */}
            {isCurrentUser && (
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1 }}>
                    <Tooltip title="Your Entry">
                        <Avatar
                            sx={{
                                bgcolor: 'primary.main',
                                width: 32,
                                height: 32,
                                borderRadius: '2px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            <PersonIcon fontSize="small" />
                        </Avatar>
                    </Tooltip>
                </Box>
            )}

            <Box
                component={RouterLink}
                to={`/single?imageId=${image.id}`}
                sx={{
                    display: 'block',
                    textDecoration: 'none',
                    color: 'inherit',
                    mb: 2
                }}
            >
                <Box
                    component="img"
                    src={image.path}
                    alt="Competition Entry"
                    sx={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: 300,
                        objectFit: 'contain',
                        borderRadius: '2px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    }}
                />
            </Box>
            <Typography 
                variant="subtitle1" 
                gutterBottom
                sx={{
                    fontWeight: 'bold',
                    color: theme.palette.primary.main
                }}
            >
                {image.species || 'Unknown Species'}
            </Typography>
            {image.weight && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Weight: {image.weight}
                </Typography>
            )}
            {image.area && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Area: {image.area}
                </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Votes: {image.voteCount || 0}
                    </Typography>
                    <Box>
                        {isCurrentUser ? (
                            <Tooltip title="You cannot vote for your own entry">
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={true}
                                        startIcon={<HowToVoteIcon />}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                                            }
                                        }}
                                    >
                                        Vote
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => onVote(image.id)}
                                disabled={hasVoted || isVoting === image.id}
                                startIcon={<HowToVoteIcon />}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                                    }
                                }}
                            >
                                {isVoting === image.id ? 'Voting...' : 'Vote'}
                            </Button>
                        )}
                    </Box>
                </Box>
                {isCurrentUser && (
                    <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={<DeleteIcon />}
                        onClick={handleRemoveClick}
                        disabled={isRemoving}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                            }
                        }}
                    >
                        {isRemoving ? 'Removing...' : 'Remove Entry'}
                    </Button>
                )}
            </Box>

            {/* Confirmation Dialog */}
            <Dialog
                open={showConfirmDialog}
                onClose={handleCloseDialog}
                aria-labelledby="remove-entry-dialog-title"
                PaperProps={{
                    sx: {
                        borderRadius: '2px',
                        p: 2,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                <DialogTitle id="remove-entry-dialog-title">
                    Remove Competition Entry?
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove this entry from the competition? This action will:
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                        <li>
                            <Typography>Remove your catch from the competition</Typography>
                        </li>
                        <li>
                            <Typography>Cancel any votes received</Typography>
                        </li>
                        <li>
                            <Typography>Allow you to enter a different catch</Typography>
                        </li>
                    </Box>
                    <Typography color="error" sx={{ mt: 2 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseDialog} 
                        color="inherit"
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleRemoveEntry}
                        color="error"
                        variant="contained"
                        disabled={isRemoving}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                            }
                        }}
                    >
                        {isRemoving ? 'Removing...' : 'Remove Entry'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

// --- Main Component ---

const CompetitionsPage = () => {
    const theme = useTheme();
    // --- State ---
    const [images, setImages] = useState<Image[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [isVoting, setIsVoting] = useState<string | null>(null);
    const [hasVotedAlready, setHasVotedAlready] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
    const [countdown, setCountdown] = useState('');
    const [competition, setCompetition] = useState<Competition | null>(null);
    const [isCompetitionLoading, setIsCompetitionLoading] = useState(true);
    const [hasEntered, setHasEntered] = useState(false);

    // --- Context ---
    const { user } = useUserContext();
    const { accessToken, uid } = user || {};
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const competitionId = queryParams.get('id');

    // --- Effects ---

    // Effect to fetch competition details and user's entry status
    useEffect(() => {
        const fetchCompetitionDetails = async () => {
            if (!competitionId) {
                setError('No competition ID provided');
                setIsCompetitionLoading(false);
                return;
            }

            try {
                setIsCompetitionLoading(true);
                const [competitions, entryStatus] = await Promise.all([
                    getCompetitions(),
                    uid ? hasUserEnteredCompetition(uid, competitionId) : Promise.resolve(false)
                ]);
                
                const selectedCompetition = competitions.find(c => c.id === competitionId);
                
                if (selectedCompetition) {
                    setCompetition(selectedCompetition);
                    setHasEntered(entryStatus);
                } else {
                    setError('Competition not found');
                }
            } catch (err) {
                logger.error('Error fetching competition details:', err);
                setError('Failed to load competition details');
            } finally {
                setIsCompetitionLoading(false);
            }
        };

        fetchCompetitionDetails();
    }, [competitionId, uid]);

    // Effect for Countdown Timer
    useEffect(() => {
        if (!competition) return;

        const updateCountdown = () => {
            const now = new Date();
            const endDate = new Date(competition.endDate);
            
            if (now > endDate) {
                setCountdown('Competition has ended');
                return;
            }
            
            const diff = endDate.getTime() - now.getTime();
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((diff / (1000 * 60)) % 60);
            const seconds = Math.floor((diff / 1000) % 60);
            
            setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s until competition ends`);
        };
        
        updateCountdown();
        const timer = setInterval(updateCountdown, 1000);
        
        return () => clearInterval(timer);
    }, [competition]);

    // Effect to Fetch Competition Data and Voting Status
    useEffect(() => {
        if (!competitionId) return;

        const fetchCompetitionData = async () => {
            setIsLoading(true);
            setError('');
            try {
                const [imagesData, votingStatus] = await Promise.allSettled([
                    getImageVotes({ competitionId }),
                    accessToken && uid 
                        ? userHasVoted(accessToken, uid, competitionId) 
                        : Promise.resolve(false)
                ]);

                if (imagesData.status === 'fulfilled' && imagesData.value?.images) {
                    setImages(imagesData.value.images);
                } else {
                    logger.error('Failed to load competition images:', imagesData.status === 'rejected' ? imagesData.reason : 'No images data');
                    setError('Could not load competition images. Please try again later.');
                    setImages([]);
                }

                if (votingStatus.status === 'fulfilled') {
                    setHasVotedAlready(votingStatus.value);
                } else if (accessToken && uid) {
                    logger.warn('Error checking userHasVoted:', votingStatus.reason);
                    setHasVotedAlready(false);
                }

            } catch (err) {
                logger.error('Unexpected error fetching competition data:', err);
                setError('An unexpected error occurred. Please refresh the page.');
                setImages([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchCompetitionData();
    }, [accessToken, uid, competitionId]);

    // Add effect to handle missing competition ID
    useEffect(() => {
        if (!competitionId) {
            navigate('/competitions/list');
        }
    }, [competitionId, navigate]);

    // --- Memoized Calculations ---

    const sortedByVotes = useMemo(() => {
        return [...images].sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0));
    }, [images]);

    const topThree = useMemo(() => sortedByVotes.slice(0, 3), [sortedByVotes]);

    // --- Event Handlers ---

    const handleVote = async (imageId: string) => {
        if (!competition) return;
        
        if (!accessToken || !uid) {
            setError('Please log in to cast your vote.');
            return;
        }
        
        if (hasVotedAlready) {
            setError('You have already cast your vote in this competition.');
            return;
        }

        setIsVoting(imageId);
        setError('');

        try {
            const voteResult = await voteForImage(accessToken, uid, imageId, competition.id);

            setImages((prevImages) =>
                prevImages.map((img) =>
                    img.id === imageId
                        ? { ...img, voteCount: typeof voteResult === 'number' ? voteResult : (img.voteCount || 0) + 1 }
                        : img
                )
            );
            setHasVotedAlready(true);

            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), CONFETTI_DURATION_MS);

        } catch (err) {
            logger.error('Voting error:', err);
            if (err instanceof Error) {
                if (err.message.includes('already voted')) {
                    setError('You have already voted in this competition.');
                    setHasVotedAlready(true);
                } else {
                    setError(err.message || 'An error occurred while submitting your vote.');
                }
            } else {
                setError('An unknown error occurred while submitting your vote.');
            }
        } finally {
            setIsVoting(null);
        }
    };

    const handleOpenRules = () => setIsRulesModalOpen(true);
    const handleCloseRules = () => setIsRulesModalOpen(false);

    // --- Render Logic ---

    if (isCompetitionLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading Competition...</Typography>
            </Box>
        );
    }

    if (!competition) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 4 }}>
                    {error || 'Competition not found'}
                </Alert>
                <Button 
                    component={RouterLink} 
                    to="/competitions" 
                    variant="contained" 
                    color="primary"
                >
                    Back to Competitions
                </Button>
            </Container>
        );
    }

    const renderContent = () => {
        if (isLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading Competition Entries...</Typography>
                </Box>
            );
        }

        if (images.length === 0) {
            return (
                <Typography sx={{ textAlign: 'center', mt: 4 }}>
                    No entries have been submitted to this competition yet.
                    Be the first!
                </Typography>
            );
        }

        return (
            <Grid container spacing={3}>
                {images.map((image) => (
                    <Grid item xs={12} sm={6} md={4} key={image.id}>
                        <ImageCard
                            image={image}
                            onVote={handleVote}
                            hasVoted={hasVotedAlready}
                            isVoting={isVoting}
                            isCurrentUser={image.user === uid}
                        />
                    </Grid>
                ))}
            </Grid>
        );
    };

    return (
        <Box sx={{ 
            py: 4, 
            background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
            minHeight: 'calc(100vh - 64px)'
        }}>
            {showConfetti && <ConfettiAnimation />}

            <Container maxWidth="lg">
                {/* Page Header */}
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        mb: 1
                    }}
                >
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{ 
                                fontWeight: 'bold', 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                flexWrap: 'wrap', 
                                justifyContent: 'center',
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                            gutterBottom
                        >
                            {competition.title}
                            <IconButton
                                color="primary"
                                onClick={handleOpenRules}
                                sx={{ ml: 1 }}
                                aria-label="Show competition rules"
                            >
                                <InfoIcon />
                            </IconButton>
                        </Typography>
                    </Box>
                </Paper>

                {/* Phase Information */}
                <PhaseInfo competition={competition} countdown={countdown} />

                {/* Action Button */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    {!hasEntered && (
                        <Button
                            component={RouterLink}
                            to={`/competitions/enter?id=${competition.id}`}
                            variant="contained"
                            color="secondary"
                            size="large"
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                                }
                            }}
                        >
                            Enter Your Catch
                        </Button>
                    )}
                    {hasEntered && (
                        <Typography variant="body1" color="text.secondary">
                            You have already entered this competition
                        </Typography>
                    )}
                </Box>

                {/* Rules Modal */}
                <Modal
                    open={isRulesModalOpen}
                    onClose={handleCloseRules}
                    aria-labelledby="competition-rules-title"
                >
                    <Box sx={{
                        ...modalStyle,
                        borderRadius: 2,
                        p: 3,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    }}>
                        <Typography 
                            id="competition-rules-title" 
                            variant="h5" 
                            component="h2" 
                            gutterBottom
                            sx={{
                                fontWeight: 'bold',
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {competition.title} – Official Rules
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Typography variant="h6" color="primary" gutterBottom>Competition Schedule</Typography>
                        <Typography variant="body1" paragraph><strong>Start Date:</strong> {formatDate(new Date(competition.startDate))}</Typography>
                        <Typography variant="body1" paragraph><strong>End Date:</strong> {formatDate(new Date(competition.endDate))}</Typography>
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="body1" paragraph><strong>Prize:</strong> {competition.prize}</Typography>
                        
                        <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>Rules</Typography>
                        {competition.rules.map((rule, index) => (
                            <Typography key={index} variant="body1" paragraph>
                                <strong>{index + 1}.</strong> {rule}
                            </Typography>
                        ))}
                        
                        <Box sx={{ textAlign: 'right', mt: 3 }}>
                            <Button 
                                variant="contained" 
                                onClick={handleCloseRules}
                                sx={{
                                    borderRadius: 1,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                                    }
                                }}
                            >
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Modal>

                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                        {error}
                    </Alert>
                )}

                {/* Render Loading, Empty, or Content */}
                {renderContent()}
            </Container>
        </Box>
    );
};

export default CompetitionsPage;